import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function AdminNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', label: 'لوحة التحكم', icon: 'ri-dashboard-line' },
    { path: '/admin/decision-center', label: 'مركز القرار', icon: 'ri-shield-check-line' },
    { path: '/admin/investors', label: 'المستثمرون', icon: 'ri-user-line' },
    { path: '/admin/investments', label: 'الاستثمارات', icon: 'ri-line-chart-line' },
    { path: '/admin/profits', label: 'الأرباح', icon: 'ri-money-dollar-circle-line' },
    { path: '/admin/profits-management', label: 'إدارة الفترات', icon: 'ri-calendar-check-line' },
    { path: '/admin/deals', label: 'الصفقات', icon: 'ri-briefcase-line' },
    { path: '/admin/capital-settings', label: 'إعدادات رأس المال', icon: 'ri-settings-3-line' },
    { path: '/admin/reports', label: 'التقارير', icon: 'ri-file-chart-line' },
    { path: '/admin/messages', label: 'الرسائل', icon: 'ri-message-3-line' },
    { path: '/admin/accountants', label: 'المحاسبين', icon: 'ri-user-settings-line' },
    { path: '/admin/board-members', label: 'مجلس الإدارة', icon: 'ri-vip-crown-line' },
    { path: '/admin/governance', label: 'الحوكمة', icon: 'ri-shield-line' },
    { path: '/admin/audit-logs', label: 'سجل التدقيق', icon: 'ri-file-list-3-line' }
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav className="bg-dark-navy/95 backdrop-blur-lg border-b border-emerald-700/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3 sm:gap-4">
              <img 
                src="https://static.readdy.ai/image/3e6f353e8dbbec4f9360c3b978f4989c/31d0a928e6e54a3d3653fca6f95d8c10.png" 
                alt="طيبات الطبية العالمية" 
                className="h-8 sm:h-10 w-auto cursor-pointer" 
                onClick={() => navigate('/admin/dashboard')}
              />
              <div className="hidden sm:block">
                <h2 className="text-base sm:text-lg font-bold text-white">طيبات الطبية</h2>
                <p className="text-xs text-gray-400">لوحة الإدارة</p>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-4">
              {menuItems.slice(0, 9).map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    isActive(item.path)
                      ? 'text-neon-cyan'
                      : 'text-gray-300 hover:text-neon-cyan'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Mobile & Desktop Actions */}
            <div className="flex items-center gap-2">
              {/* Logout Button - Always Visible */}
              <button
                onClick={handleLogout}
                className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-logout-box-line"></i>
                <span className="hidden sm:inline">تسجيل الخروج</span>
              </button>

              {/* Hamburger Menu Button - Mobile Only */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors cursor-pointer"
                aria-label="القائمة"
              >
                <i className={`text-xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Dropdown Menu */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsMenuOpen(false)}
          ></div>

          {/* Menu Panel */}
          <div className="lg:hidden fixed top-[60px] left-0 right-0 bg-[#0A0E27] border-b border-gray-800 z-40 max-h-[calc(100vh-60px)] overflow-y-auto">
            <div className="py-2">
              {menuItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setIsMenuOpen(false);
                  }}
                  className={`w-full px-6 py-3 flex items-center gap-3 transition-colors cursor-pointer ${
                    isActive(item.path)
                      ? 'bg-cyan-500/20 text-cyan-400 border-r-4 border-cyan-500'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <i className={`${item.icon} text-xl`}></i>
                  <span className="font-semibold">{item.label}</span>
                </button>
              ))}
            </div>

            {/* قائمة الأدوات */}
            <div className="border-t border-gray-700/50 pt-2">
              <a
                href="/admin/fix-investors-auth"
                className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700/30 hover:text-white rounded-lg transition-all cursor-pointer"
              >
                <i className="ri-user-settings-line text-xl"></i>
                <span>إصلاح حساب مستثمر</span>
              </a>
              <a
                href="/admin/repair-investors"
                className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700/30 hover:text-white rounded-lg transition-all cursor-pointer"
              >
                <i className="ri-tools-line text-xl"></i>
                <span>إصلاح شامل للمستثمرين</span>
              </a>
            </div>
          </div>
        </>
      )}
    </>
  );
}
